<script setup>
import { DEV_MODE } from '@/config/dev-mode';
import { CONTENTS, CONTENTS_PAGE_MAP } from './constants';

import { useAnalysisStore } from '@/store/modules/analysis';
import { addContentLogAPI } from '@/api/logServiceApi';
import { postDailyFortuneRateAPI, getDailyFortuneRateAPI } from '@/api/calendarApi';
import { fetchBaseServiceAPI } from '@/api/baseServiceApi';
import { useYongsinLightData } from '@/composables/yongsinLight/useYongsinLightData';

import { useRoute } from 'vue-router';
import ContentsYongsinLight01 from './ContentsYongsinLight01.vue';
import ContentsYongsinLight02 from './ContentsYongsinLight02.vue';
import ContentsYongsinLight03 from './ContentsYongsinLight03.vue';
import ContentsYongsinLight04 from './ContentsYongsinLight04.vue';
import ContentsYongsinLight05 from './ContentsYongsinLight05.vue';
import ContentsYongsinLight06 from './ContentsYongsinLight06.vue';
import ContentsList from '@/components/saju-insight/common/ContentsList.vue';
import AnalysisLoadingOverlay from '@/components/saju-insight/common/AnalysisLoadingOverlay.vue';

const props = defineProps({
  hasPermission: {
    type: Boolean,
  },
  loading: {
    type: Boolean,
    default: false,
  },
});
const emit = defineEmits(['goPopAnalysis']);

const route = useRoute();
const store = useAnalysisStore();
const guestId = route.params.id;

// =============== 로딩 상태 ===============
const isLoadingData = ref(false);

// =============== API 데이터 ===============
const apiRawData = computed(() => store.yongsinLightApiData);

const fetchYongsinLightData = async () => {
  if (!props.hasPermission) return;
  isLoadingData.value = true;
  store.setYongsinLightApiData(null);
  try {
    const result = await fetchBaseServiceAPI({
      guestUserId: guestId,
      suggestionFlag: 2,
      analysisTypeId: 41,
    });
    if (result?.success) {
      store.setYongsinLightApiData(result.data);
    }
  } catch (e) {
    console.error('[YongsinLight] API fetch failed:', e);
  } finally {
    isLoadingData.value = false;
  }
};

const { content02Data, content03Data, content04Data, content05Data, content06Data, quickUsageData } =
  useYongsinLightData(apiRawData);

// =============== 페이지 ===============
/** 페이지 이동 */
const moveToPage = (id) => {
  if (id > 1 && !props.hasPermission) {
    emit('goPopAnalysis');
    return;
  }
  indexNo.value = id;
};

/** index값으로 페이지 이동 (목차 클릭 또는 페이지네이션) */
const updateIndexNo = (id, tocIdx) => {
  if (tocIdx != null) {
    addContentLogAPI({
      contentType: props.hasPermission ? 'ay_yongsin_light_toc_paid_opt3' : 'ay_yongsin_light_toc_free_opt3',
      option3: String(tocIdx),
    });
  } else {
    addContentLogAPI({
      contentType: 'ay_yongsin_light_page_opt3',
      option3: String(id),
    });
  }
  moveToPage(id);
};

// 인덱스
const indexNo = ref(1);
watch(indexNo, (newIndex) => {
  store.setYongsinLightIndex(newIndex);
});
onUnmounted(() => {
  store.setYongsinLightIndex(1);
});

watch(
  () => store.selectedContentIndex,
  (newIdx) => {
    const target = CONTENTS_PAGE_MAP[newIdx];
    if (target) moveToPage(target);
  },
  { immediate: true },
);

/** 표지로 이동 */
const goToOverView = () => {
  store.clickMainMenu('/overviewYsLight');
};

// =============== 평가하기 데이터 ===============
const ratingResult = ref(0);
const ratingStatus = ref(false);

const getYongsinLightRate = async () => {
  if (!props.hasPermission) return;

  try {
    const params = {
      serviceName: 'YONGSIN_LIGHT_ANALYSIS',
      objectKey: `${guestId}`,
    };

    const result = await getDailyFortuneRateAPI(params);

    if (result) {
      ratingResult.value = result.score;
      ratingStatus.value = true;
    }
  } catch (e) {
    if (e?.response?.status === 404) {
      ratingResult.value = 0;
      ratingStatus.value = false;
    }
  }
};

const postYongsinLightRate = async (score) => {
  try {
    const params = {
      score,
      serviceName: 'YONGSIN_LIGHT_ANALYSIS',
      objectKey: `${guestId}`,
    };

    const result = await postDailyFortuneRateAPI(params);

    if (result) {
      ratingStatus.value = true;
      getYongsinLightRate();
    }
  } catch (error) {}
};

onMounted(() => {
  getYongsinLightRate();
  fetchYongsinLightData();
});

// 새로고침 시 hasPermission이 늦게 true로 바뀌는 경우 대비
const isInitialPermissionLoad = ref(true);
watch(
  () => props.hasPermission,
  (newVal, oldVal) => {
    if (newVal && !store.yongsinLightApiData) {
      fetchYongsinLightData();
      getYongsinLightRate();
    }
    // 구매 완료 시 유료 콘텐츠 시작 페이지(2페이지)로 이동
    // 초기 로드(새로고침)에서는 건너뛰고, 결제 후 변경일 때만 이동
    if (newVal && !oldVal && !isInitialPermissionLoad.value) {
      indexNo.value = 2;
    }
    isInitialPermissionLoad.value = false;
  },
);
</script>

<template>
  <div v-if="store.selectedMenu.endsWith('ys-light')" class="full-height">
    <AnalysisLoadingOverlay
      :loading="isLoadingData || props.loading"
      :message="'용신 라이트 분석중입니다!'"
    />

    <ContentsYongsinLight01
      v-if="indexNo === 1"
      class="full-height"
    />

    <ContentsYongsinLight02
      v-if="indexNo === 2"
      class="full-height"
      :contentData="content02Data"
    />

    <ContentsYongsinLight03
      v-if="indexNo === 3"
      class="full-height"
      :contentData="content03Data"
    />

    <ContentsYongsinLight04
      v-if="indexNo === 4"
      class="full-height"
      :contentData="content04Data"
    />

    <ContentsYongsinLight05
      v-if="indexNo === 5"
      class="full-height"
      :contentData="content05Data"
    />

    <ContentsYongsinLight06
      v-if="indexNo === 6"
      class="full-height"
      :contentData="content06Data"
      :ratingResult="ratingResult"
      :ratingStatus="ratingStatus"
      @postRate="postYongsinLightRate"
      @goToOverView="goToOverView"
    />
  </div>

  <div class="contentsListBtnBox">
    <ContentsList
      :items="CONTENTS"
      :curPage="indexNo"
      :lastPage="6"
      :hasPermission="hasPermission"
      @updateIndexNo="updateIndexNo"
      @goToOverView="goToOverView"
    />
  </div>
</template>

<style>
@import '/src/assets/nangman_style/personality.scss';
</style>
